import { ref, computed } from 'vue';
import { onAuthStateChange, signOutUser, getCurrentUser } from '../firebase/auth';
import { showNotification } from '../stores/notification';
import http from '../http-common';

const user = ref(null);
const isAuthenticated = ref(false);
const userRole = ref(null);
const userClaims = ref(null);

// Role monitoring
let roleMonitoringInterval = null;
const ROLE_CHECK_INTERVAL = 60000; // Check every 60 seconds

// Token refresh monitoring
let tokenRefreshInterval = null;
const TOKEN_REFRESH_INTERVAL = 3000000; // Refresh every 50 minutes (50 * 60 * 1000)

// Start periodic token refresh
const startTokenRefresh = () => {
  if (tokenRefreshInterval) {
    clearInterval(tokenRefreshInterval);
  }
  
  tokenRefreshInterval = setInterval(async () => {
    const currentUser = getCurrentUser();
    if (currentUser) {
      try {
        const idTokenResult = await currentUser.getIdTokenResult(true); // Force refresh
        userClaims.value = idTokenResult.claims;
        userRole.value = idTokenResult.claims.role || null;
        
        if (user.value) {
          user.value = {
            ...user.value,
            role: userRole.value,
            accountOwnerId: idTokenResult.claims.accountOwnerId,
            userId: idTokenResult.claims.userId,
          };
        }
      } catch (error) {
        console.error('Error in periodic token refresh:', error);
      }
    }
  }, TOKEN_REFRESH_INTERVAL);
};

// Listen to auth state changes
onAuthStateChange(async (firebaseUser) => {
  const previousUserId = user.value?.uid || null;
  
  if (firebaseUser) {
    // If user changed, clear patient store cache
    if (previousUserId && previousUserId !== firebaseUser.uid) {
      try {
        const { usePatientStore } = await import('../components/composables/usePatientStore.js');
        const store = usePatientStore();
        store.clearCache();
      } catch (error) {
      }
    }
    
    // Get custom claims
    try {
      const idTokenResult = await firebaseUser.getIdTokenResult();
      userClaims.value = idTokenResult.claims;
      userRole.value = idTokenResult.claims.role || null;
      
      
      // For magic link users, email might be null on the Firebase user object
      // but it's available in custom claims
      const userEmail = firebaseUser.email || idTokenResult.claims.email;
      
      user.value = {
        uid: firebaseUser.uid,
        email: userEmail,
        displayName: firebaseUser.displayName || userEmail?.split('@')[0],
        photoURL: firebaseUser.photoURL,
        role: userRole.value,
        accountOwnerId: idTokenResult.claims.accountOwnerId,
        userId: idTokenResult.claims.userId,
      };
      
    } catch (error) {
      console.error('Error getting user claims:', error);
      user.value = {
        uid: firebaseUser.uid,
        email: firebaseUser.email,
        displayName: firebaseUser.displayName || firebaseUser.email?.split('@')[0],
        photoURL: firebaseUser.photoURL,
        role: null,
        accountOwnerId: null,
        userId: null,
      };
    }
    isAuthenticated.value = true;
    
    // Start periodic token refresh when user logs in
    startTokenRefresh();
  } else {
    user.value = null;
    userRole.value = null;
    userClaims.value = null;
    isAuthenticated.value = false;
    
    // Stop token refresh when user logs out
    if (tokenRefreshInterval) {
      clearInterval(tokenRefreshInterval);
      tokenRefreshInterval = null;
    }
  }
});

export const useAuth = () => {
  const signOut = async () => {
    const result = await signOutUser();
    if (result.success) {
      user.value = null;
      userRole.value = null;
      userClaims.value = null;
      isAuthenticated.value = false;
    }
    return result;
  };

  // Force refresh ID token to get updated custom claims
  const refreshUserClaims = async () => {
    const currentUser = getCurrentUser();
    if (!currentUser) {
      return false;
    }

    try {
      
      // First, reload the user to ensure we have the latest Firebase state
      await currentUser.reload();
      
      // Force refresh the ID token (true = force refresh from server)
      const idTokenResult = await currentUser.getIdTokenResult(true);
      
      const previousRole = userRole.value;
      userClaims.value = idTokenResult.claims;
      userRole.value = idTokenResult.claims.role || null;
      
      // Update user object with new role
      if (user.value) {
        user.value = {
          ...user.value,
          role: userRole.value,
          accountOwnerId: idTokenResult.claims.accountOwnerId,
          userId: idTokenResult.claims.userId,
        };
      }
      
      return true;
    } catch (error) {
      console.error('Error refreshing claims:', error);
      return false;
    }
  };

  // Permission helpers
  const isViewer = computed(() => {
    const result = userRole.value === 'viewer';
    return result;
  });
  const isEditor = computed(() => userRole.value === 'editor');
  const isAdmin = computed(() => false); // Admin role removed - kept for backwards compatibility
  const isAccountOwner = computed(() => !userRole.value); // Account owner has no role claim (superuser)
  const isSiteAdmin = computed(() => {
    const result = userClaims.value?.siteadmin === true;
    return result;
  }); // Site admin has siteadmin custom claim
  
  const canEdit = computed(() => {
    const result = isAccountOwner.value || isEditor.value;
    return result;
  });
  
  const canManageUsers = computed(() => {
    return isAccountOwner.value; // Only superuser/account owner
  });

  // Check for role changes (for invited users only)
  const checkRoleChange = async () => {
    const currentUser = getCurrentUser();
    if (!currentUser) return;

    // Only check for invited users
    const isInvitedUser = currentUser.uid.match(/^user_\d+_/);
    if (!isInvitedUser) return;

    try {
      
      // Fetch current role from database
      const response = await http.get(`/users/current`);
      
      const dbRole = response.data.role;
      const dbStatus = response.data.status;
      
      
      // Check if user was deleted or deactivated
      if (dbStatus === 'deleted' || dbStatus === 'inactive') {
        
        // Stop monitoring
        stopRoleMonitoring();
        
        // Show notification
        showNotification(
          `Your account has been ${dbStatus === 'deleted' ? 'removed' : 'deactivated'}. You will be signed out now.`,
          'error',
          3000,
          false // Not closeable
        );
        
        // Sign out after 3 seconds
        setTimeout(async () => {
          
          // Clear patient cache
          try {
            const { usePatientStore } = await import('../components/composables/usePatientStore.js');
            const store = usePatientStore();
            store.clearCache();
          } catch (error) {
          }
          
          // Sign out
          await signOut();
          
          // Reload page
          window.location.reload();
        }, 3000);
        
        return;
      }
      
      console.log('🔍 Comparison details:', {
        dbRoleType: typeof dbRole,
        firebaseRoleType: typeof userRole.value,
        dbRoleValue: dbRole,
        firebaseRoleValue: userRole.value,
        areEqual: dbRole === userRole.value
      });
      
      // Compare with current Firebase token role
      if (dbRole !== userRole.value) {
        
        // Stop monitoring to prevent multiple triggers
        stopRoleMonitoring();
        
        // Import notification store for updating message
        const { notificationStore } = await import('../stores/notification');
        
        // Show countdown notification (non-closeable, stays until logout)
        let countdown = 10;
        showNotification(
          `Your role has been updated to "${dbRole}". You will be signed out in ${countdown} seconds to apply the changes.`,
          'info',
          10000,
          false // Not closeable
        );
        
        // Update countdown every second (reuse same notification, no flickering)
        const countdownInterval = setInterval(() => {
          countdown--;
          if (countdown > 0) {
            // Update the message without creating a new notification
            notificationStore.message.value = 
              `Your role has been updated to "${dbRole}". You will be signed out in ${countdown} second${countdown > 1 ? 's' : ''} to apply the changes.`;
          }
        }, 1000);
        
        // Sign out after 10 seconds
        setTimeout(async () => {
          clearInterval(countdownInterval);
          
          // Clear patient cache
          try {
            const { usePatientStore } = await import('../components/composables/usePatientStore.js');
            const store = usePatientStore();
            store.clearCache();
          } catch (error) {
          }
          
          // Sign out
          await signOut();
          
          // Reload page to show login screen with fresh state
          window.location.reload();
        }, 10000);
      }
    } catch (error) {
      // Check if user was deleted (404 error)
      if (error.response?.status === 404) {
        
        // Stop monitoring
        stopRoleMonitoring();
        
        // Show notification
        showNotification(
          'Your account has been removed. You will be signed out now.',
          'error',
          3000,
          false // Not closeable
        );
        
        // Sign out after 3 seconds
        setTimeout(async () => {
          
          // Clear patient cache
          try {
            const { usePatientStore } = await import('../components/composables/usePatientStore.js');
            const store = usePatientStore();
            store.clearCache();
          } catch (error) {
          }
          
          // Sign out
          await signOut();
          
          // Reload page
          window.location.reload();
        }, 3000);
        
        return;
      }
      
      // Log other errors for debugging
      console.error('❌ Error checking role:', error);
      console.error('❌ Error response:', error.response);
      console.error('❌ Error status:', error.response?.status);
      console.error('❌ Error data:', error.response?.data);
    }
  };

  // Start monitoring role changes
  const startRoleMonitoring = () => {
    const currentUser = getCurrentUser();
    
    
    if (!currentUser) {
      return;
    }

    // Only monitor invited users
    const isInvitedUser = currentUser.uid.match(/^user_\d+_/);
    
    if (!isInvitedUser) {
      return;
    }

    // Stop any existing interval
    stopRoleMonitoring();

    
    // Check immediately
    checkRoleChange();
    
    // Then check periodically
    roleMonitoringInterval = setInterval(checkRoleChange, ROLE_CHECK_INTERVAL);
  };

  // Stop monitoring role changes
  const stopRoleMonitoring = () => {
    if (roleMonitoringInterval) {
      clearInterval(roleMonitoringInterval);
      roleMonitoringInterval = null;
    }
  };

  return {
    user,
    isAuthenticated,
    signOut,
    refreshUserClaims,
    getCurrentUser,
    userRole,
    userClaims,
    isViewer,
    isEditor,
    isAdmin,
    isAccountOwner,
    isSiteAdmin,
    canEdit,
    canManageUsers,
    startRoleMonitoring,
    stopRoleMonitoring
  };
};
